import React, { useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { motion, AnimatePresence } from 'framer-motion'
import { withConversationState } from '../../utils/navigationHelpers.ts'
import { X } from 'lucide-react'
import { useConversationalAgent } from '../../contexts/ConversationalAgentContext.tsx'
import {
  ProjectAutofillDraftSendInterstitial,
  useProjectAutofillDraftSendGate
} from '../Dashboard/DashboardHeader/ConversationalMode/ProjectAutofillDraftSendInterstitial.tsx'
import { getDesktopSuggestions } from '../Dashboard/DashboardHeader/dashboardActionsConfig.tsx'
import ConversationWindow from '../Dashboard/DashboardHeader/ConversationalMode/ConversationWindow/ConversationWindow.tsx'
import ConversationInput from '../Dashboard/DashboardHeader/ConversationalMode/ConversationInput/ConversationInput.tsx'
import AIAgentOrbLoader from '../Dashboard/AIAgentOrb/AIAgentOrbLoader.tsx'
import { matchRoute, getRouteLabel } from '../../routes/routeConfig.ts'
import { FloatingAgentMessageContext } from './FloatingAgentMessageContext.tsx'
import { pendingAttachmentFallbackCaption } from '../../utils/agentImageStorage.ts'
import {
  AGENT_NAMES,
  isProjectAutofillAgentId,
  SUGGESTED_ACTION_ID_TO_AGENT,
  suggestedChipSkipsActiveAgentOverride
} from '../../constants/agentNames.ts'
import { getProfileContextualSuggestion } from './profileTabPrompts.ts'
import styles from './FloatingAgent.module.scss'

const FloatingAgent: React.FC = () => {
  const location = useLocation()
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const {
    currentSession,
    sendMessage,
    isProcessing,
    canSendMessage,
    remainingMessages,
    hasReachedLimit,
    limit,
    userId,
    userData,
    missingFields,
    readyToExecute,
    activeAgent,
    collectedData,
    hasPendingImage,
    imageUploadAcknowledged,
    pendingImagePreview,
    pendingAttachmentMime,
    documentPipelineLoopActive,
    documentPipelinePhase,
    storePendingImage,
    clearPendingImage
  } = useConversationalAgent()

  const draftSendGate = useProjectAutofillDraftSendGate()

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const navigateFromConversation = useMemo(() => withConversationState(navigate), [navigate])

  const allSuggestions = useMemo(
    () => getDesktopSuggestions(navigateFromConversation, userId, userData, dispatch),
    [navigateFromConversation, userId, userData, dispatch]
  )

  const sessionMessages = currentSession?.messages

  const isProjectAutofillFlow = useMemo(() => {
    const list = sessionMessages ?? []
    const latestAssistantMessage = [...list].reverse().find((m) => m.role === 'assistant')
    let latestActiveAgentFromLLM: string | null = null
    if (latestAssistantMessage?.llmRawResponse) {
      try {
        latestActiveAgentFromLLM = JSON.parse(latestAssistantMessage.llmRawResponse)?.activeAgent || null
      } catch {
        latestActiveAgentFromLLM = null
      }
    }
    return (
      collectedData?.intent === 'project_autofill' ||
      latestAssistantMessage?.actionId === 'project-autofill' ||
      isProjectAutofillAgentId(latestActiveAgentFromLLM)
    )
  }, [sessionMessages, collectedData?.intent])

  const messages = sessionMessages ?? []

  const routeMatch = useMemo(() => {
    const matches = matchRoute(location.pathname)
    if (matches.length === 0) return null
    const { node, params } = matches[0]
    if (node.key === 'dashboard') return null
    return { node, params }
  }, [location.pathname])

  const contextualEmpty = useMemo(() => {
    if (!routeMatch) return null
    if (routeMatch.node.key === 'perfil') {
      const searchParams = new URLSearchParams(location.search)
      const tabParam = searchParams.get('tab')
      const profileId = routeMatch.params?.id
      const isOwnProfile =
        profileId != null &&
        userId != null &&
        String(profileId) === String(userId)
      const suggestion = getProfileContextualSuggestion(tabParam, isOwnProfile)
      return suggestion ? { label: suggestion.label, prompt: suggestion.prompt } : null
    }
    if (!routeMatch.node.suggestionPrompt) return null
    return {
      label: getRouteLabel(routeMatch.node, routeMatch.params),
      prompt: routeMatch.node.suggestionPrompt
    }
  }, [routeMatch, location.search, userId])

  const handleSend = async () => {
    if ((!inputValue.trim() && !hasPendingImage) || isProcessing || !canSendMessage) {
      return
    }
    const payload =
      inputValue.trim() ||
      (hasPendingImage ? pendingAttachmentFallbackCaption(pendingAttachmentMime) : '')
    const gateHeld = draftSendGate.tryHoldSend(payload)
    if (gateHeld) {
      return
    }
    setInputValue('')
    await sendMessage(payload)
  }

  const showImageUpload =
    activeAgent === AGENT_NAMES.projectAutofillAgent ||
    isProjectAutofillFlow ||
    (missingFields?.includes('image_uploaded') ?? false) ||
    (readyToExecute && collectedData?.intent === 'create_post')

  const handleConfirmExecution = async () => {
    if (isProcessing) return
    setInputValue('')
    await sendMessage('', { confirmExecution: true })
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleSuggestionChipClick = () => {
    if (!contextualEmpty?.prompt || isProcessing || !canSendMessage) return
    const p = contextualEmpty.prompt
    if (draftSendGate.tryHoldSend(p)) {
      return
    }
    void sendMessage(p)
  }

  return (
    <>
      {!isPanelOpen && (
        <button
          type="button"
          className={styles.trigger}
          onClick={() => setIsPanelOpen(true)}
          aria-label="Abrir asistente con IA"
          title="Abrir asistente (hacé clic)"
        >
          <AIAgentOrbLoader status="idle" className={styles.triggerOrb} />
        </button>
      )}

      <AnimatePresence>
        {isPanelOpen && (
          <motion.div
            className={styles.panel}
            role="dialog"
            aria-label="Asistente con IA"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] }}
            style={{ transformOrigin: 'bottom right' }}
          >
            <div className={styles.header}>
            <span className={styles.headerTitle}>Asistente IA de Edify</span>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={() => setIsPanelOpen(false)}
              aria-label="Cerrar"
            >
              <X size={18} />
            </button>
          </div>
          <div className={styles.content}>
            <div className={styles.messagesWrap}>
              <div className={styles.messagesSlot}>
              {messages.length === 0 && !isProcessing ? (
                contextualEmpty ? (
                  <div className={styles.emptyStateContextual}>
                    <p className={styles.emptyStateLabel}>
                      Estás en {contextualEmpty.label}.
                    </p>
                    <button
                      type="button"
                      className={styles.suggestionChip}
                      onClick={handleSuggestionChipClick}
                      disabled={!canSendMessage}
                    >
                      {contextualEmpty.prompt}
                    </button>
                    <p className={styles.emptyStateHint}>
                      Usá la sugerencia o escribí lo que quieras.
                    </p>
                  </div>
                ) : (
                  <div className={styles.emptyStateGeneric}>
                    <p className={styles.emptyStateText}>
                      Hacé tu primera pregunta y te ayudo a encontrar lo que necesitás.
                    </p>
                  </div>
                )
              ) : (
                <FloatingAgentMessageContext.Provider value={true}>
                  <ConversationWindow
                    variant="floating"
                    messages={messages}
                    onClose={() => setIsPanelOpen(false)}
                    isLoading={isProcessing}
                    matchedSuggestion={null}
                    onRequestGuide={() => {
                      const p = 'Ayudame a hacerlo'
                      if (draftSendGate.tryHoldSend(p)) {
                        return
                      }
                      void sendMessage(p)
                    }}
                    allSuggestions={allSuggestions}
                    hasReachedLimit={hasReachedLimit}
                    remainingMessages={remainingMessages}
                    limit={limit}
                    userData={userData}
                    pendingImagePreview={pendingImagePreview}
                    isLatestMessageGuardarPost={messages[messages.length - 1]?.actionId === 'guardar-post'}
                    showDocumentPipelineLoop={documentPipelineLoopActive}
                    documentPipelinePhase={documentPipelinePhase}
                    onSuggestedChipSend={(label, actionId) => {
                      if (draftSendGate.tryHoldSend(label)) {
                        return
                      }
                      const override =
                        suggestedChipSkipsActiveAgentOverride(actionId)
                          ? undefined
                          : SUGGESTED_ACTION_ID_TO_AGENT[actionId]
                      void sendMessage(
                        label,
                        override ? { activeAgentOverride: override } : undefined
                      )
                    }}
                  />
                </FloatingAgentMessageContext.Provider>
              )}
              </div>
            </div>
            <div className={styles.inputWrap}>
              <ConversationInput
                value={inputValue}
                onChange={setInputValue}
                onSend={handleSend}
                onKeyPress={handleKeyPress}
                disabled={isProcessing || !canSendMessage}
                placeholder={
                  canSendMessage
                    ? 'Escribí tu mensaje...'
                    : 'Has alcanzado el límite de mensajes'
                }
                showImageUpload={showImageUpload}
                allowVideo={collectedData?.intent === 'create_post'}
                allowDocuments={activeAgent === AGENT_NAMES.projectAutofillAgent || isProjectAutofillFlow}
                hasPendingImage={hasPendingImage}
                imageUploadAcknowledged={imageUploadAcknowledged}
                pendingImagePreview={pendingImagePreview}
                pendingAttachmentMime={pendingAttachmentMime}
                onImageSelect={storePendingImage}
                onImageClear={clearPendingImage}
                readyToExecute={readyToExecute}
                onConfirmExecution={readyToExecute ? handleConfirmExecution : undefined}
              />
            </div>
          </div>
          </motion.div>
        )}
      </AnimatePresence>
      <ProjectAutofillDraftSendInterstitial
        open={draftSendGate.heldPayload != null}
        payload={draftSendGate.heldPayload ?? ''}
        getProjectAutofillDraftGuardApi={draftSendGate.getProjectAutofillDraftGuardApi}
        onClearInputAndDismissGate={() => {
          setInputValue('')
          draftSendGate.dismiss()
        }}
        onSendMessage={async (msg) => {
          await sendMessage(msg)
        }}
        onCancel={draftSendGate.dismiss}
      />
    </>
  )
}

export default FloatingAgent
